import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useMaybeSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage, TextInput } from "src/components";
import { MortgagePaymentsValue } from "src/types/Application";
import { noInput } from "src/utilities/input";

import { enums, formatters } from "@fraction/shared";

const { formatDebtType } = formatters.debt;

export interface MortgagePaymentsProps {
  onChange: (value: MortgagePaymentsValue) => void;
  value?: MortgagePaymentsValue;
  propertyAddress?: string;
  lenderName: string;
  mortgageType?: enums.DebtType;
  isSubpage?: boolean;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const MortgagePayments = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  propertyAddress,
  lenderName,
  onChange,
  mortgageType = enums.DebtType.MORTGAGE,
  isSubpage = false,
  ...props
}: MortgagePaymentsProps) => {
  const validate = useCallback(() => !noInput(value?.paymentAmount), [value]);
  const { handleNext, nextDisabled } = useMaybeSubmit({ onNext, validate, shouldSubmit: !isSubpage });

  const handleNextFinal = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();

      // We default to monthly frequency. We don't allow that input, but we might at some point,
      // so we will keep the frequency option here.
      if (!value?.frequency) {
        onChange({
          ...value,
          frequency: enums.Frequency.MONTHLY,
        });
      }

      handleNext();
    },
    [value, onChange, handleNext]
  );

  const handleChangePaymentAmount = useCallback(
    (paymentAmount?: string) => {
      onChange({
        ...value,
        paymentAmount,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title={`What are your ${lenderName} ${formatDebtType(mortgageType)} monthly payments like?`}
      description={`What are the payments for your ${lenderName} ${formatDebtType(mortgageType)} at ${
        propertyAddress || "your property"
      } on a monthly basis?`}
      onNext={handleNextFinal}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <TextInput
        ref={focusRef}
        name="paymentAmount"
        label="Payment amount"
        defaultValue={value?.paymentAmount || ""}
        onChange={handleChangePaymentAmount}
        min={0}
        max={1000000}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
    </FormFlowPage>
  );
};

export default memo(MortgagePayments);
